import Card, { CardHeader, CardBody } from '../ui/Card';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { adressenApi } from '../../api/adressen';
import { einheitenApi, type EinheitDetail } from '../../api/einheiten';
import { haeuserApi, type HausDetail, HAUS_TYPEN } from '../../api/haeuser';
import { parseAddress } from '../../utils/address';
import { useIsModuleEnabled } from '../../hooks/useConfigVisibility';
import type { Adresse, Haus, Einheit } from '../../types/index';

interface SaveResult {
  adresseId: number;
  hausId: number | null;
  einheitId: number | null;
  address: string;
  zipcode: string | null;
  city: string | null;
}

interface HierarchyChangeData {
  adresse_id?: number | null;
  haus_id?: number | null;
  einheit_id?: number | null;
  address_verified?: boolean;
}

interface AddressHierarchySectionProps {
  /** Unique ID of the owning entity — used for TanStack Query cache keys */
  entityId: number;
  isAssigned: boolean;
  initialAdresseId?: number | null;
  initialHausId?: number | null;
  initialEinheitId?: number | null;
  /** Raw address text to parse into strasse + hausnummer */
  prefillAddress?: string | null;
  prefillZipcode?: string | null;
  prefillCity?: string | null;
  /** Scraped-data hints shown when address is not yet assigned (InseratDetail) */
  scrapedHint?: { address: string | null; zipcode: string | null; city: string | null } | null;
  /** Pre-fill values for the Einheit form (InseratDetail scraped data) */
  prefillEinheitTyp?: string | null;
  prefillGuestsCount?: number | null;
  prefillBedrooms?: number | null;
  prefillSizeSqm?: string | null;
  /**
   * Called after adressenApi.createHierarchy succeeds.
   * Parent is responsible for patching the entity with the returned IDs.
   */
  onSave: (result: SaveResult) => Promise<void>;
  /**
   * Called for granular hierarchy link/unlink operations (Haus select, Einheit unlink, etc).
   * Parent patches the entity with the provided fields.
   */
  onHierarchyChange: (data: HierarchyChangeData) => void;
}

export default function AddressHierarchySection({
  entityId,
  isAssigned,
  initialHausId,
  initialEinheitId,
  prefillAddress,
  prefillZipcode,
  prefillCity,
  scrapedHint,
  prefillEinheitTyp,
  prefillGuestsCount,
  prefillBedrooms,
  prefillSizeSqm,
  onSave,
  onHierarchyChange,
}: AddressHierarchySectionProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  // "Standort- und Objektdaten" is anchored on the Adressen module; hide the whole
  // section when it is disabled. Haus/Objekt sub-blocks follow their own modules.
  const adressenVisible = useIsModuleEnabled('adressen');
  const haeuserEnabled = useIsModuleEnabled('haeuser');
  const objekteEnabled = useIsModuleEnabled('objekte');

  const parsed = parseAddress(prefillAddress || '');
  const [strasse, setStrasse] = useState(parsed.strasse);
  const [hausnummer, setHausnummer] = useState(parsed.hausnummer);
  const [plz, setPlz] = useState(prefillZipcode || '');
  const [stadt, setStadt] = useState(prefillCity || '');
  const [linkedAdresse, setLinkedAdresse] = useState<Adresse | null>(null);
  const [saveError, setSaveError] = useState('');
  const [savingAddress, setSavingAddress] = useState(false);
  const [savingHaus, setSavingHaus] = useState(false);
  const [savingEinheit, setSavingEinheit] = useState(false);

  const [hausOverride, setHausOverride] = useState<HausDetail | null>(null);
  const [einheitOverride, setEinheitOverride] = useState<EinheitDetail | null>(null);

  // Search for matching Adresse when already assigned
  const { data: matchedAdresse } = useQuery({
    queryKey: ['adresse-match', entityId, prefillAddress, prefillZipcode],
    queryFn: async () => {
      if (!isAssigned) return null;
      const p = parseAddress(prefillAddress || '');
      if (!p.strasse) return null;
      const results = await adressenApi.search(p.strasse);
      return results.find((a) => a.strasse === p.strasse && a.hausnummer === p.hausnummer) || null;
    },
    enabled: isAssigned,
  });

  const displayAdresse = linkedAdresse || matchedAdresse;

  // Restore linked Einheit
  const { data: linkedEinheitDetail } = useQuery({
    queryKey: ['einheit', initialEinheitId],
    queryFn: () => einheitenApi.getEinheit(initialEinheitId!),
    enabled: !!initialEinheitId,
  });

  // Restore linked Haus from Einheit or direct haus link
  const linkedHausId = linkedEinheitDetail?.haus_id ?? initialHausId;
  const { data: linkedHausDetail } = useQuery({
    queryKey: ['haus', linkedHausId],
    queryFn: () => haeuserApi.getHaus(linkedHausId!),
    enabled: !!linkedHausId,
  });

  const activeHaus = hausOverride ?? linkedHausDetail ?? null;
  const activeEinheit = einheitOverride ?? linkedEinheitDetail ?? null;

  // Existing Häuser at the linked Adresse
  const { data: existingHaeuser } = useQuery({
    queryKey: ['adresse-haeuser', displayAdresse?.id],
    queryFn: () => adressenApi.getHaeuser(displayAdresse!.id),
    enabled: !!displayAdresse,
  });

  // Existing Einheiten at the active Haus
  const { data: existingEinheiten } = useQuery({
    queryKey: ['haus-einheiten', activeHaus?.id],
    queryFn: () => adressenApi.getEinheiten(activeHaus!.id),
    enabled: !!activeHaus,
  });

  // Haus form
  const [hausForm, setHausForm] = useState({ typ: '', bezeichnung: '', baugenehmigung: '', notes: '' });
  useEffect(() => {
    if (activeHaus) {
      setHausForm({
        typ: activeHaus.typ ?? '',
        bezeichnung: activeHaus.bezeichnung ?? '',
        baugenehmigung: activeHaus.baugenehmigung ?? '',
        notes: activeHaus.notes ?? '',
      });
    } else {
      setHausForm({ typ: '', bezeichnung: '', baugenehmigung: '', notes: '' });
    }
  }, [activeHaus]);

  // Einheit form
  const [einheitForm, setEinheitForm] = useState({
    bezeichnung: '',
    typ: prefillEinheitTyp ?? '',
    guests_count: prefillGuestsCount?.toString() ?? '',
    bedrooms: prefillBedrooms?.toString() ?? '',
    size_sqm: prefillSizeSqm ?? '',
    notes: '',
  });
  useEffect(() => {
    if (activeEinheit) {
      setEinheitForm({
        bezeichnung: activeEinheit.bezeichnung,
        typ: activeEinheit.typ ?? '',
        guests_count: activeEinheit.guests_count?.toString() ?? '',
        bedrooms: activeEinheit.bedrooms?.toString() ?? '',
        size_sqm: activeEinheit.size_sqm?.toString() ?? '',
        notes: activeEinheit.notes ?? '',
      });
    } else {
      setEinheitForm({
        bezeichnung: '',
        typ: prefillEinheitTyp ?? '',
        guests_count: prefillGuestsCount?.toString() ?? '',
        bedrooms: prefillBedrooms?.toString() ?? '',
        size_sqm: prefillSizeSqm ?? '',
        notes: '',
      });
    }
  }, [activeEinheit, prefillBedrooms, prefillEinheitTyp, prefillGuestsCount, prefillSizeSqm]);

  const hausIsExisting = !!(activeHaus && hausForm.bezeichnung.trim() === (activeHaus.bezeichnung ?? ''));
  const einheitIsExisting = !!(activeEinheit && einheitForm.bezeichnung.trim() === activeEinheit.bezeichnung);

  // Dirty tracking for unsaved changes warning on browser navigation
  const isHausDirty = activeHaus
    ? (hausForm.typ !== (activeHaus.typ ?? '') ||
       hausForm.bezeichnung !== (activeHaus.bezeichnung ?? '') ||
       hausForm.baugenehmigung !== (activeHaus.baugenehmigung ?? '') ||
       hausForm.notes !== (activeHaus.notes ?? ''))
    : (hausForm.typ !== '' || hausForm.bezeichnung.trim() !== '');
  const isEinheitDirty = activeEinheit
    ? (einheitForm.bezeichnung !== activeEinheit.bezeichnung ||
       einheitForm.typ !== (activeEinheit.typ ?? '') ||
       einheitForm.guests_count !== (activeEinheit.guests_count?.toString() ?? '') ||
       einheitForm.bedrooms !== (activeEinheit.bedrooms?.toString() ?? '') ||
       einheitForm.size_sqm !== (activeEinheit.size_sqm?.toString() ?? '') ||
       einheitForm.notes !== (activeEinheit.notes ?? ''))
    : einheitForm.bezeichnung.trim() !== '';
  const isDirty = isHausDirty || isEinheitDirty;

  useEffect(() => {
    if (!isDirty) return;
    const handler = (e: BeforeUnloadEvent) => { e.preventDefault(); };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  // ── Actions ──

  const saveAddress = async () => {
    if (!strasse.trim() || !hausnummer.trim()) {
      setSaveError('Straße und Hausnummer sind erforderlich.');
      return;
    }
    setSaveError('');
    setSavingAddress(true);
    try {
      const hierarchyData: Parameters<typeof adressenApi.createHierarchy>[0] = {
        strasse: strasse.trim(),
        hausnummer: hausnummer.trim(),
        plz: plz.trim() || undefined,
        stadt: stadt.trim() || undefined,
      };
      if (hausForm.bezeichnung.trim()) hierarchyData.haus_bezeichnung = hausForm.bezeichnung.trim();
      if (einheitForm.bezeichnung.trim()) {
        hierarchyData.einheit_bezeichnung = einheitForm.bezeichnung.trim();
        if (einheitForm.typ) hierarchyData.einheit_typ = einheitForm.typ;
      }

      const result = await adressenApi.createHierarchy(hierarchyData);

      await onSave({
        adresseId: result.adresse.id,
        hausId: result.haus?.id ?? null,
        einheitId: result.einheit?.id ?? null,
        address: `${strasse.trim()} ${hausnummer.trim()}`,
        zipcode: plz.trim() || null,
        city: stadt.trim() || null,
      });

      setLinkedAdresse(result.adresse);
      setHausOverride(null);
      setEinheitOverride(null);
      queryClient.invalidateQueries({ queryKey: ['adressen'] });
      if (result.einheit) queryClient.invalidateQueries({ queryKey: ['einheit', result.einheit.id] });
      if (result.haus) queryClient.invalidateQueries({ queryKey: ['haus', result.haus.id] });
    } catch {
      setSaveError('Fehler beim Speichern der Adresse.');
    } finally {
      setSavingAddress(false);
    }
  };

  const saveHaus = async () => {
    setSavingHaus(true);
    setSaveError('');
    try {
      if (hausIsExisting && activeHaus) {
        const updated = await haeuserApi.updateHaus(activeHaus.id, {
          bezeichnung: hausForm.bezeichnung.trim(),
          baugenehmigung: hausForm.baugenehmigung.trim() || null,
          notes: hausForm.notes.trim() || null,
        });
        setHausOverride(updated);
        onHierarchyChange({ haus_id: activeHaus.id });
        queryClient.invalidateQueries({ queryKey: ['haus', activeHaus.id] });
      } else {
        if (!displayAdresse) { setSaveError('Bitte zuerst eine Adresse speichern.'); return; }
        const created = await haeuserApi.createHaus({
          adresse_id: displayAdresse.id,
          typ: hausForm.typ || 'Einfamilienhaus',
          bezeichnung: hausForm.bezeichnung.trim() || undefined,
          baugenehmigung: hausForm.baugenehmigung.trim() || undefined,
          notes: hausForm.notes.trim() || undefined,
        });
        setHausOverride(created);
        setEinheitOverride(null);
        onHierarchyChange({ haus_id: created.id, einheit_id: null });
        queryClient.invalidateQueries({ queryKey: ['adresse-haeuser', displayAdresse.id] });
      }
    } catch {
      setSaveError('Fehler beim Speichern des Hauses.');
    } finally {
      setSavingHaus(false);
    }
  };

  const saveEinheit = async () => {
    if (!einheitForm.bezeichnung.trim() || !activeHaus) return;
    setSavingEinheit(true);
    setSaveError('');
    try {
      const parsedData = {
        bezeichnung: einheitForm.bezeichnung.trim(),
        typ: einheitForm.typ || null,
        guests_count: einheitForm.guests_count ? parseInt(einheitForm.guests_count) : null,
        bedrooms: einheitForm.bedrooms ? parseInt(einheitForm.bedrooms) : null,
        size_sqm: einheitForm.size_sqm ? parseFloat(einheitForm.size_sqm) : null,
        notes: einheitForm.notes.trim() || null,
      };
      if (einheitIsExisting && activeEinheit) {
        const updated = await einheitenApi.updateEinheit(activeEinheit.id, parsedData);
        setEinheitOverride(updated);
        onHierarchyChange({ einheit_id: activeEinheit.id, haus_id: activeHaus.id });
        queryClient.invalidateQueries({ queryKey: ['einheit', activeEinheit.id] });
      } else {
        const created = await einheitenApi.createEinheit({
          haus_id: activeHaus.id,
          bezeichnung: parsedData.bezeichnung,
          typ: parsedData.typ ?? undefined,
          guests_count: parsedData.guests_count ?? undefined,
          bedrooms: parsedData.bedrooms ?? undefined,
          size_sqm: parsedData.size_sqm ?? undefined,
          notes: parsedData.notes ?? undefined,
        });
        setEinheitOverride(created);
        onHierarchyChange({ einheit_id: created.id, haus_id: activeHaus.id });
        queryClient.invalidateQueries({ queryKey: ['haus-einheiten', activeHaus.id] });
      }
    } catch {
      setSaveError('Fehler beim Speichern des Objekts.');
    } finally {
      setSavingEinheit(false);
    }
  };

  const selectHaus = (haus: Haus) => {
    haeuserApi.getHaus(haus.id).then((detail) => {
      setHausOverride(detail);
      setEinheitOverride(null);
      onHierarchyChange({ haus_id: haus.id, einheit_id: null });
    });
  };

  const selectEinheit = (einheit: Einheit) => {
    einheitenApi.getEinheit(einheit.id).then((detail) => {
      setEinheitOverride(detail);
      onHierarchyChange({ einheit_id: detail.id });
    });
  };

  const unlinkAddress = () => {
    setLinkedAdresse(null);
    setHausOverride(null);
    setEinheitOverride(null);
    onHierarchyChange({ address_verified: false, adresse_id: null, einheit_id: null, haus_id: null });
  };

  const unlinkHaus = () => {
    setHausOverride(null);
    setEinheitOverride(null);
    onHierarchyChange({ haus_id: null, einheit_id: null });
  };

  const unlinkEinheit = () => {
    setEinheitOverride(null);
    onHierarchyChange({ einheit_id: null });
  };

  const inputClass = 'w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 disabled:text-gray-500 outline-none';

  if (!adressenVisible) return null;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-3 flex-wrap">
          <h3 className="text-lg font-medium">Standort- und Objektdaten</h3>
          <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium leading-4 ${
            isAssigned ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'
          }`}>
            <span className={`w-1.5 h-1.5 rounded-full ${isAssigned ? 'bg-green-500' : 'bg-amber-500'}`} />
            Adresse
          </span>
          {haeuserEnabled && (
            <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium leading-4 ${
              activeHaus ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
            }`}>
              <span className={`w-1.5 h-1.5 rounded-full ${activeHaus ? 'bg-green-500' : 'bg-gray-400'}`} />
              Haus
            </span>
          )}
          {objekteEnabled && (
            <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium leading-4 ${
              activeEinheit ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
            }`}>
              <span className={`w-1.5 h-1.5 rounded-full ${activeEinheit ? 'bg-green-500' : 'bg-gray-400'}`} />
              Objekt
            </span>
          )}
        </div>
      </CardHeader>

      <CardBody className="space-y-4">
        {/* ── Adresse ── */}
        <h4 className="text-sm font-medium text-gray-700">Adresse</h4>

        {scrapedHint && !isAssigned && (scrapedHint.address || scrapedHint.zipcode || scrapedHint.city) && (
          <div className="bg-blue-50 border border-blue-100 rounded-lg px-4 py-3">
            <p className="text-xs font-medium text-blue-700 mb-1">Extrahierte Daten (Koordinaten)</p>
            <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm text-blue-800">
              {scrapedHint.address && <span>{scrapedHint.address}</span>}
              {scrapedHint.zipcode && <span>PLZ: {scrapedHint.zipcode}</span>}
              {scrapedHint.city && <span>{scrapedHint.city}</span>}
            </div>
          </div>
        )}

        {isAssigned && displayAdresse && (
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate(`/adressen/${displayAdresse.id}`)}
              className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium leading-4 bg-green-100 text-green-800 hover:bg-green-200 transition-colors"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
              {displayAdresse.strasse} {displayAdresse.hausnummer}{displayAdresse.plz && `, ${displayAdresse.plz}`}{displayAdresse.stadt && ` ${displayAdresse.stadt}`} &rarr;
            </button>
            <button onClick={unlinkAddress} className="btn btn-text text-neutral-600 enabled:hover:text-neutral-800 focus-visible:ring-neutral-500 text-xs">
              Trennen
            </button>
          </div>
        )}

        {!isAssigned && (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Straße</label>
                <input value={strasse} onChange={(e) => setStrasse(e.target.value)} placeholder="z.B. Musterstraße" className={inputClass} />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Hausnummer</label>
                <input value={hausnummer} onChange={(e) => setHausnummer(e.target.value)} placeholder="z.B. 5a" className={inputClass} />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">PLZ</label>
                <input value={plz} onChange={(e) => setPlz(e.target.value)} placeholder="z.B. 12345" className={inputClass} />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Stadt</label>
                <input value={stadt} onChange={(e) => setStadt(e.target.value)} placeholder="z.B. Berlin" className={inputClass} />
              </div>
            </div>
            <div className="flex justify-end">
              <button
                onClick={saveAddress}
                disabled={!strasse.trim() || !hausnummer.trim() || savingAddress}
                className="btn btn-primary"
              >
                {savingAddress ? 'Wird gespeichert...' : 'Speichern'}
              </button>
            </div>
            {saveError && <p className="text-sm text-red-600">{saveError}</p>}
          </>
        )}

        {haeuserEnabled && (
        <>
        <div className="border-t border-gray-200" />

        {/* ── Haus ── */}
        <div className="space-y-3">
          <h4 className="text-sm font-medium text-gray-700">Haus</h4>

          {isAssigned && activeHaus && (
            <div className="flex items-center gap-2">
              <button
                onClick={() => navigate(`/haeuser/${activeHaus.id}`)}
                className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium leading-4 bg-green-100 text-green-800 hover:bg-green-200 transition-colors"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
                {activeHaus.bezeichnung || `Haus #${activeHaus.id}`} &rarr;
              </button>
              <button onClick={unlinkHaus} className="btn btn-text text-neutral-600 enabled:hover:text-neutral-800 focus-visible:ring-neutral-500 text-xs">Trennen</button>
            </div>
          )}

          {isAssigned && displayAdresse && !activeHaus && existingHaeuser && existingHaeuser.length > 0 && (
            <div>
              <p className="text-xs text-gray-500 mb-2">Vorhandene Häuser an dieser Adresse:</p>
              <div className="flex flex-wrap gap-2">
                {existingHaeuser.map((h) => (
                  <button
                    key={h.id}
                    onClick={() => selectHaus(h)}
                    className="px-3 py-1.5 text-sm rounded-full transition-colors bg-gray-100 text-gray-700 hover:bg-blue-100 hover:text-blue-800"
                  >
                    {h.bezeichnung}
                  </button>
                ))}
              </div>
            </div>
          )}

          {isAssigned && displayAdresse && !activeHaus && (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Typ</label>
                  <select value={hausForm.typ} onChange={(e) => setHausForm({ ...hausForm, typ: e.target.value })} className={inputClass}>
                    <option value="">– Typ wählen –</option>
                    {HAUS_TYPEN.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Bezeichnung (optional)</label>
                  <input value={hausForm.bezeichnung} onChange={(e) => setHausForm({ ...hausForm, bezeichnung: e.target.value })} placeholder="z.B. Vorderhaus, Hinterhaus" className={inputClass} />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Baugenehmigung</label>
                  <input value={hausForm.baugenehmigung} onChange={(e) => setHausForm({ ...hausForm, baugenehmigung: e.target.value })} placeholder="Nicht angegeben" className={inputClass} />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Notizen</label>
                <textarea value={hausForm.notes} onChange={(e) => setHausForm({ ...hausForm, notes: e.target.value })} placeholder="Optionale Notizen zum Haus" rows={2} className={inputClass} />
              </div>
              <div className="flex justify-end">
                <button onClick={saveHaus} disabled={savingHaus} className="btn btn-primary">
                  {savingHaus ? 'Wird gespeichert...' : 'Anlegen'}
                </button>
              </div>
            </>
          )}

          {!isAssigned && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Typ</label>
                <select value={hausForm.typ} onChange={(e) => setHausForm({ ...hausForm, typ: e.target.value })} className={inputClass}>
                  <option value="">– Typ wählen –</option>
                  {HAUS_TYPEN.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Bezeichnung (optional)</label>
                <input value={hausForm.bezeichnung} onChange={(e) => setHausForm({ ...hausForm, bezeichnung: e.target.value })} placeholder="z.B. Vorderhaus, Hinterhaus" className={inputClass} />
              </div>
            </div>
          )}
        </div>
        </>
        )}

        {objekteEnabled && (
        <>
        <div className="border-t border-gray-200" />

        {/* ── Objekt (Einheit) ── */}
        <div className="space-y-3">
          <h4 className="text-sm font-medium text-gray-700">Objekt</h4>

          {isAssigned && activeEinheit && (
            <div className="flex items-center gap-2">
              <button
                onClick={() => navigate(`/objekte/${activeEinheit.id}`)}
                className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium leading-4 bg-green-100 text-green-800 hover:bg-green-200 transition-colors"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
                {activeEinheit.bezeichnung}{activeEinheit.typ && ` (${activeEinheit.typ})`} &rarr;
              </button>
              <button onClick={unlinkEinheit} className="btn btn-text text-neutral-600 enabled:hover:text-neutral-800 focus-visible:ring-neutral-500 text-xs">Trennen</button>
            </div>
          )}

          {isAssigned && activeHaus && !activeEinheit && existingEinheiten && existingEinheiten.length > 0 && (
            <div>
              <p className="text-xs text-gray-500 mb-2">Vorhandene Objekte in diesem Haus:</p>
              <div className="flex flex-wrap gap-2">
                {existingEinheiten.map((e) => (
                  <button
                    key={e.id}
                    onClick={() => selectEinheit(e)}
                    className="px-3 py-1.5 text-sm rounded-full transition-colors bg-gray-100 text-gray-700 hover:bg-blue-100 hover:text-blue-800"
                  >
                    {e.bezeichnung}{e.typ && ` (${e.typ})`}
                  </button>
                ))}
              </div>
            </div>
          )}

          {isAssigned && activeHaus && !activeEinheit && (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Bezeichnung</label>
                  <input value={einheitForm.bezeichnung} onChange={(e) => setEinheitForm({ ...einheitForm, bezeichnung: e.target.value })} placeholder="z.B. Wohnung EG links" className={inputClass} />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Typ</label>
                  <select value={einheitForm.typ} onChange={(e) => setEinheitForm({ ...einheitForm, typ: e.target.value })} className={inputClass}>
                    <option value="">Nicht angegeben</option>
                    <option value="wohnung">Wohnung</option>
                    <option value="privatzimmer">Privatzimmer</option>
                    <option value="gewerbe">Gewerbe</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Gästeanzahl</label>
                  <input type="number" value={einheitForm.guests_count} onChange={(e) => setEinheitForm({ ...einheitForm, guests_count: e.target.value })} placeholder="z.B. 4" className={inputClass} />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Schlafzimmer</label>
                  <input type="number" value={einheitForm.bedrooms} onChange={(e) => setEinheitForm({ ...einheitForm, bedrooms: e.target.value })} placeholder="z.B. 2" className={inputClass} />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Größe (m²)</label>
                  <input type="number" step="0.1" value={einheitForm.size_sqm} onChange={(e) => setEinheitForm({ ...einheitForm, size_sqm: e.target.value })} placeholder="z.B. 65.5" className={inputClass} />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Notizen</label>
                <textarea value={einheitForm.notes} onChange={(e) => setEinheitForm({ ...einheitForm, notes: e.target.value })} placeholder="Optionale Notizen zum Objekt" rows={2} className={inputClass} />
              </div>
              <div className="flex justify-end">
                <button onClick={saveEinheit} disabled={!einheitForm.bezeichnung.trim() || savingEinheit} className="btn btn-primary">
                  {savingEinheit ? 'Wird gespeichert...' : 'Anlegen'}
                </button>
              </div>
            </>
          )}

          {isAssigned && !activeHaus && (
            <p className="text-xs text-gray-400 italic">Bitte zuerst ein Haus auswählen oder anlegen.</p>
          )}

          {!isAssigned && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Bezeichnung</label>
                <input value={einheitForm.bezeichnung} onChange={(e) => setEinheitForm({ ...einheitForm, bezeichnung: e.target.value })} placeholder="z.B. Wohnung EG links (optional)" className={inputClass} />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Typ</label>
                <select value={einheitForm.typ} onChange={(e) => setEinheitForm({ ...einheitForm, typ: e.target.value })} className={inputClass}>
                  <option value="">Nicht angegeben</option>
                  <option value="wohnung">Wohnung</option>
                  <option value="privatzimmer">Privatzimmer</option>
                  <option value="gewerbe">Gewerbe</option>
                </select>
              </div>
            </div>
          )}
        </div>
        </>
        )}
      </CardBody>
    </Card>
  );
}
